import React, { useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Tooltip,
  Box,
  alpha,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../utils/export/componentExporter";

const SummaryItem = ({
  label,
  value,
  tooltip,
  isHighlighted = false,
  isFinal = false,
}) => {
  const formatAmount = (amount) => {
    const num = Number(amount) || 0;

    const formatted = Math.abs(num).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return num < 0 ? `(${formatted})` : formatted;
  };

  return (
    <Box
      sx={{
        px: isHighlighted ? 2 : 0.75,
        py: isHighlighted ? 1.75 : 0.75,
        borderRadius: 2,
        bgcolor: (theme) =>
          isFinal
            ? alpha(theme.palette.primary.main, 0.09)
            : isHighlighted
              ? alpha(theme.palette.primary.main, 0.05)
              : "transparent",
        transition: "background-color 0.2s ease",
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
        },
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        spacing={2}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing={0.75}
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant='body1'
            sx={{
              fontWeight: isHighlighted ? 700 : 600,
              color: isHighlighted ? "primary.main" : "text.secondary",
            }}
          >
            {label}
          </Typography>

          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 15,
                  color: "text.disabled",
                  cursor: "help",
                }}
              />
            </Tooltip>
          )}
        </Stack>

        <Typography
          variant={isFinal ? "subtitle1" : "body2"}
          sx={{
            fontWeight: isHighlighted ? 700 : 500,
            color: isHighlighted ? "primary.main" : "text.primary",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(value)}
        </Typography>
      </Stack>
    </Box>
  );
};

const PlSummary = ({
  revenueTotal,
  costOfSalesTotal,
  otherIncomeTotal,
  expensesTotal,
  netProfitBeforeTax,
}) => {
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "P&L Summary");
    }
  };

  return (
    <Card
      sx={{
        mt: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0px 4px 20px rgba(96, 63, 151, 0.06)",
        bgcolor: "background.paper",
      }}
      role='region'
      aria-labelledby='financial-summary-title'
      ref={exportRef}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={2}
          sx={{
            mb: 3,
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <AccountBalanceOutlinedIcon color='primary' />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='h5'
                fontWeight={600}
                id='financial-summary-title'
                color='text.primary'
              >
                Detailed Profit & Loss Summary
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <CameraExportButton
              onClick={handleExportAsImage}
              tooltipTitle='Screenshot of the Financial Summary'
            />
          </Box>
        </Stack>

        <Stack spacing={2}>
          <SummaryItem label='Total Revenue' value={revenueTotal} />

          <SummaryItem label='Total Cost of Sales' value={costOfSalesTotal} />

          <SummaryItem
            label='Gross Profit'
            value={revenueTotal + costOfSalesTotal}
            isHighlighted
          />

          <SummaryItem label='Total Other Income' value={otherIncomeTotal} />

          <SummaryItem label='Total Expenses' value={expensesTotal} />

          <Divider sx={{ borderStyle: "dashed", my: 0.5 }} />

          <SummaryItem
            label='Net Profit / (Loss) before Tax'
            value={netProfitBeforeTax}
            isHighlighted
            isFinal
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PlSummary;
